import { v4 as uuidv4 } from 'uuid';
import type { ResultsVehicleRow } from '../../types/simulation';
import type { Point } from '../../context/context';
import type { Fleet, Vehicle } from '../../components/map/components/FleetMenu';
import type { OperatingPeriod } from '../../types/simulation';

export interface ResultsVehiclesToFleetInput {
  rows: ResultsVehicleRow[];
  runId: string;
  scenarioName?: string;
  serviceDate?: string | null;
  existingPoints?: Point[];
  targetLayerId: number;
}

export interface ResultsVehiclesToFleetResult {
  fleet: Fleet | null;
  depotPoints: Point[];
  warnings: string[];
  vehiclesCreated: number;
  fallbackDepotCount: number;
}

type ShiftGroup = {
  key: string;
  vehicleId: string;
  shiftId: string;
  rows: ResultsVehicleRow[];
};

const coordKey = (lat: number, lng: number): string => `${lat.toFixed(6)}:${lng.toFixed(6)}`;

function numeric(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim() !== '') {
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

function hasCoordPair(lat: number | null, lng: number | null): lat is number {
  return lat != null && lng != null && lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180;
}

function toClockTime(raw: string | null | undefined, fallback: string): string {
  if (!raw || raw.trim() === '') return fallback;
  const value = raw.trim();
  const match = value.match(/(?:T|\s)?(\d{2}):(\d{2})(?::\d{2})?/);
  if (!match) return fallback;
  return `${match[1]}:${match[2]}`;
}

function stableSuffix(id: string): string {
  if (!id) return 'unknown';
  const trimmed = id.trim();
  return trimmed.length <= 8 ? trimmed : trimmed.slice(-8);
}

function buildGroups(rows: ResultsVehicleRow[]): ShiftGroup[] {
  const grouped = new Map<string, ShiftGroup>();
  rows.forEach((row, index) => {
    const vehicleId = String(row.vehicle_id ?? `vehicle-${index + 1}`).trim();
    const shiftId = String(row.vehicle_shift_id ?? `${vehicleId}-shift-1`).trim();
    const key = `${vehicleId}::${shiftId}`;
    const existing = grouped.get(key);
    if (existing) {
      existing.rows.push(row);
    } else {
      grouped.set(key, { key, vehicleId, shiftId, rows: [row] });
    }
  });
  return Array.from(grouped.values());
}

function seatsFromRows(rows: ResultsVehicleRow[]): { standardSeats: number; wheelchairSeats: number } {
  let standardSeats = 0;
  let wheelchairSeats = 0;
  rows.forEach((row) => {
    const seatCount = Math.max(0, Math.floor(numeric(row.seats) ?? 0));
    const capacityType = String(row.capacity_type ?? '').toLowerCase();
    if (capacityType.includes('wheel')) {
      wheelchairSeats += seatCount;
    } else if (capacityType.includes('standard') || capacityType === '') {
      standardSeats += seatCount;
    }
  });
  return { standardSeats, wheelchairSeats };
}

function createDepotPoint(
  name: string,
  lat: number,
  lng: number,
  targetLayerId: number,
): Point {
  return {
    id: `sim-depot-${uuidv4()}`,
    layerId: targetLayerId,
    type: 'point',
    name,
    description: 'Imported simulation depot',
    colour: '#FF9800',
    icon: 'location',
    size: 24,
    isLocked: false,
    coordinates: [lat, lng],
    hidden: true,
  };
}

export function transformResultsVehiclesToFleet({
  rows,
  runId,
  scenarioName,
  serviceDate,
  existingPoints = [],
  targetLayerId,
}: ResultsVehiclesToFleetInput): ResultsVehiclesToFleetResult {
  if (!rows.length) {
    return {
      fleet: null,
      depotPoints: [],
      warnings: ['Fleet not created (no vehicles data).'],
      vehiclesCreated: 0,
      fallbackDepotCount: 0,
    };
  }

  const warnings: string[] = [];
  const createdPoints: Point[] = [];
  const pointIdByCoord = new Map<string, string>();
  existingPoints.forEach((point) => pointIdByCoord.set(coordKey(point.coordinates[0], point.coordinates[1]), point.id));

  const groups = buildGroups(rows);
  let firstValidDepotPointId: string | null = null;
  let fallbackDepotCount = 0;
  const vehicles: Vehicle[] = [];

  const getOrCreatePointId = (lat: number, lng: number, name: string): string => {
    const key = coordKey(lat, lng);
    const existingId = pointIdByCoord.get(key);
    if (existingId) return existingId;
    const point = createDepotPoint(name, lat, lng, targetLayerId);
    createdPoints.push(point);
    pointIdByCoord.set(key, point.id);
    return point.id;
  };

  groups.forEach((group) => {
    const first = group.rows[0];
    const startLat = numeric(first.start_address_latitude);
    const startLng = numeric(first.start_address_longitude);
    const endLat = numeric(first.end_address_latitude);
    const endLng = numeric(first.end_address_longitude);
    const startTime = toClockTime(first.start_time, '06:00');
    const endTime = toClockTime(first.end_time, '22:00');
    const { standardSeats, wheelchairSeats } = seatsFromRows(group.rows);
    const totalSeats = standardSeats + wheelchairSeats;

    let startLocation = '';
    let endLocation = '';
    if (hasCoordPair(startLat, startLng) && startLng != null) {
      const startLatValue = startLat;
      const startLngValue = startLng;
      startLocation = getOrCreatePointId(startLatValue, startLngValue, `Depot Start ${stableSuffix(group.vehicleId)}`);
      firstValidDepotPointId ||= startLocation;
    }
    if (hasCoordPair(endLat, endLng) && endLng != null) {
      const endLatValue = endLat;
      const endLngValue = endLng;
      endLocation = getOrCreatePointId(endLatValue, endLngValue, `Depot End ${stableSuffix(group.vehicleId)}`);
      firstValidDepotPointId ||= endLocation;
    }
    if (!startLocation && firstValidDepotPointId) {
      startLocation = firstValidDepotPointId;
      fallbackDepotCount += 1;
    }
    if (!endLocation && firstValidDepotPointId) {
      endLocation = firstValidDepotPointId;
      fallbackDepotCount += 1;
    }

    if (totalSeats <= 0) {
      warnings.push(`Skipped ${group.key}: no seats.`);
      return;
    }
    if (!startLocation || !endLocation) {
      warnings.push(`Skipped ${group.key}: missing depot coordinates (no fallback available).`);
      return;
    }
    const period: OperatingPeriod = {
      start: startTime,
      end: endTime,
      breaks: [],
      serviceType: 'on-demand',
    };
    if (!period.start || !period.end) {
      warnings.push(`Skipped ${group.key}: missing operating period.`);
      return;
    }

    vehicles.push({
      id: `sim-fleet-vehicle-${uuidv4()}`,
      name: `Sim ${stableSuffix(group.vehicleId)} (${stableSuffix(group.shiftId)})`,
      standardSeats,
      wheelchairSeats,
      startLocation,
      endLocation,
      operatingHours: [period],
      numberOfVehicles: 1,
      uatVehicleId: group.vehicleId,
      uatMetadata: {
        originalId: group.vehicleId,
        originalName: first.vehicle_type ?? undefined,
        importedAt: Date.now(),
        importedFrom: 'simulation_results',
        serviceDate: serviceDate ?? undefined,
        shifts: group.rows.map((row) => ({
          vehicle_shift_id: row.vehicle_shift_id,
          start_time: row.start_time,
          end_time: row.end_time,
          start_address_latitude: row.start_address_latitude,
          start_address_longitude: row.start_address_longitude,
          end_address_latitude: row.end_address_latitude,
          end_address_longitude: row.end_address_longitude,
        })),
      },
    });
  });

  if (!vehicles.length) {
    return {
      fleet: null,
      depotPoints: createdPoints,
      warnings: warnings.length ? warnings : ['Fleet not created: no valid simulator vehicles found.'],
      vehiclesCreated: 0,
      fallbackDepotCount,
    };
  }

  const fleetNameSuffix = scenarioName?.trim() || stableSuffix(runId);
  const fleet: Fleet = {
    id: `sim-fleet-${uuidv4()}`,
    name: `Sim Fleet ${fleetNameSuffix}`,
    vehicles,
    uatMetadata: {
      importedAt: Date.now(),
      importedFrom: 'simulation_results',
      serviceDate: serviceDate ?? undefined,
      sourceVehicleIds: Array.from(new Set(vehicles.map((vehicle) => vehicle.uatVehicleId ?? vehicle.id))),
      runId,
      source: 'simulation_results',
    } as Fleet['uatMetadata'] & { runId: string; source: 'simulation_results' },
  };

  if (fallbackDepotCount > 0) {
    warnings.push(`Applied depot fallback ${fallbackDepotCount} time(s).`);
  }

  return {
    fleet,
    depotPoints: createdPoints,
    warnings,
    vehiclesCreated: vehicles.length,
    fallbackDepotCount,
  };
}

export function mergeImportedFleet(existingFleets: Fleet[], importedFleet: Fleet): { fleets: Fleet[]; replacedEmptyFleet: boolean } {
  if (existingFleets.length === 1 && existingFleets[0].vehicles.length === 0) {
    return { fleets: [{ ...importedFleet, id: existingFleets[0].id }], replacedEmptyFleet: true };
  }
  return { fleets: [...existingFleets, importedFleet], replacedEmptyFleet: false };
}
